import { useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "../UI/popover";
import { Button } from "../UI/button";
import { Command, CommandInput, CommandList, CommandItem, CommandGroup, CommandEmpty } from "../UI/command";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "../../Lib/Cn";
import MobileFullScreenDropdown from "./MobileDropdown";
import useIsMobile from "../../Hooks/useIsMobile";

export function CommonFilter({
  key,
  label,
  options,
  value,
  onChange,
  displayValue,
  renderOption, // NEW: Custom render function for options
  searchEnabled = true,
  disabled = false,
  showClearButton = true,
  hideAllOption = false,
  hideLabelOption = false, // NEW: Skip showing label as an option in dropdown
  isMobileDropdown = true,
  mobileDropdownText,
  tLabel,
  mobileLabel = "",
  categories, // NEW: Support hierarchical categories
}) {
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategoryForMobile, setSelectedCategoryForMobile] = useState(null); // Track expanded category for hierarchical mobile view
  const isActive = value && value !== "all";

  // Logic: Specifically check if the value is "This Month"
  const isThisMonth = value === "This Month";
  const isLargeFilter = label === 'All Next Actions' || label === 'All Activity';

  // Check if we have hierarchical data (categories) or flat options
  const isHierarchical = categories && categories.length > 0;

  const popoverWidth = isHierarchical || isLargeFilter ? "w-[235px]" : "w-[200px]";

  // Logic: Show clear button ONLY if it is active AND NOT "This Month"
  const canShowClear = showClearButton && isActive && !isThisMonth;

  // FIX: Dynamic search placeholder for Desktop
  const searchPlaceholder = `Search ${mobileLabel || label}`;

  // Build flat options array from hierarchical or flat structure
  const getFlatOptions = () => {
    if (isHierarchical) {
      const flatOpts = [];
      categories.forEach((cat) => {
        if (cat.subFilters && cat.subFilters.length > 0) {
          flatOpts.push({ ...cat, isCategory: true, indent: 0 });
          cat.subFilters.forEach((subFilter) => {
            flatOpts.push({ ...subFilter, isSubFilter: true, indent: 1, parentValue: cat.value });
          });
        } else {
          flatOpts.push({ ...cat, indent: 0 });
        }
      });
      return flatOpts;
    }
    return options || [];
  };

  const flatOptions = getFlatOptions();

  const filteredOptions = flatOptions.filter((option) => {
    const optionText =
      typeof option === "object"
        ? option.name ||
        option.label ||
        option.value ||
        option.Typ ||
        option.CNm ||
        ""
        : option || "";
    return optionText
      .toString()
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
  });

  const getDisplay = () => {
    if (!value || value === "all") return tLabel ? tLabel : label;
    if (displayValue) return displayValue(value);

    if (isHierarchical) {
      for (const category of categories) {
        if (category.value === value) return category.name;
        if (category.subFilters) {
          for (const subFilter of category.subFilters) {
            if (subFilter.value === value) return subFilter.displayValue || subFilter.name;
          }
        }
      }
    }

    if (options) {
      for (const option of options) {
        const optValue = getOptionValue(option);
        if (optValue === value) return getOptionLabel(option);
      }
    }

    return typeof value === "object" ? value.name : value;
  };

  const getOptionValue = (option) => {
    if (typeof option === "object") {
      return option.ID || option.id || option.value || option.name || option.Typ || option.CNm;
    }
    return option;
  };
  const handleClearAction = (e) => {
    e.stopPropagation();
    // If it's a period filter (identified by mobileLabel), reset to "This Month"
    if (mobileLabel === "Period") {
      onChange("This Month");
    } else {
      onChange("all");
    }
  };
  const getOptionLabel = (option) => {
    return typeof option === "object" ? (option.name || option.label || option.value || option.Typ || option.CNm || '') : (option || '');
  };

  // --- FIX START: Check if the current label exists inside the options list ---
  const isLabelInOptions = (options || []).some((option) => getOptionLabel(option) === label);
  // --- FIX END ---

  const defaultRenderOption = (option) => {
    return getOptionLabel(option);
  };

  const renderOptionContent = renderOption || defaultRenderOption;

  const normalizedOptions = (options || []).map((opt) => ({
    ...opt,
    __label: getOptionLabel(opt),
    __value: getOptionValue(opt),
  }));

  if (disabled) {
    return null;
  }

  if (isMobile & isMobileDropdown) {
    if (isHierarchical) {
      return (
        <div className="w-full flex flex-col gap-3">
          {/* Title for hierarchical filter */}
          <h3 className="text-sm font-semibold text-gray-700">
            {mobileDropdownText || label}
          </h3>

          {/* Buttons container */}
          <div className="w-full flex flex-wrap gap-2">
            {/* Main category buttons */}
            {!hideAllOption && (
              <button
                onClick={() => {
                  onChange("all");
                  setSelectedCategoryForMobile(null);
                }}
                className={cn(
                  "inline-flex items-center justify-center gap-2 whitespace-nowrap sahi-radius ring-offset-background focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 h-10 px-3 py-1 text-xs font-normal border transition-colors duration-200 w-fit",
                  value === "all"
                    ? "bg-sahi-green text-white border-sahi-green"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100 hover:text-gray-800"
                )}
              >
                <span className="flex items-center gap-1">{label}</span>
              </button>
            )}

            {/* Main categories */}
            {categories.map((category) => (
              <button
                key={category.value}
                onClick={() => {
                  onChange(category.value);
                  setSelectedCategoryForMobile(null);
                }}
                className={cn(
                  "inline-flex items-center justify-center gap-2 whitespace-nowrap sahi-radius ring-offset-background focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 h-10 px-3 py-1 text-xs font-normal border transition-colors duration-200 w-fit",
                  value === category.value
                    ? "bg-sahi-green text-white border-sahi-green"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100 hover:text-gray-800"
                )}
              >
                <span className="flex items-center gap-1">{category.name}</span>
              </button>
            ))}

            {/* All sub-filters shown independently */}
            {categories.map((category) =>
              category.subFilters && category.subFilters.length > 0 && (
                category.subFilters.map((subFilter) => (
                  <button
                    key={subFilter.value}
                    onClick={() => {
                      onChange(subFilter.value);
                      setSelectedCategoryForMobile(null);
                    }}
                    className={cn(
                      "inline-flex items-center justify-center gap-2 whitespace-nowrap sahi-radius ring-offset-background focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 h-10 px-3 py-1 text-xs font-normal border transition-colors duration-200 w-fit",
                      value === subFilter.value
                        ? "bg-sahi-green text-white border-sahi-green"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100 hover:text-gray-800"
                    )}
                  >
                    <span className="flex items-center gap-1">{subFilter.displayValue || subFilter.name}</span>
                  </button>
                ))
              )
            )}
          </div>
        </div>
      );
    }

    const mobileOptsList = (options || []).map((opt) => ({
      ...opt,
      __label: getOptionLabel(opt),
      __value: getOptionValue(opt),
    }));

    return (
      <MobileFullScreenDropdown
        options={mobileOptsList}
        // FIX 1 Cont: Ensure the passed value matches the stringified options
        value={value ? String(value) : value}
        onSelect={(selectedValue) => {
          onChange(selectedValue);
        }}
        placeholder={label}
        displayKey="__label"
        valueKey="__value"
        allowAdd={false}
        className="!w-fit max-w-[220px] bg-white text-black rounded-[5px] !h-8 lg:!h-10"
        disabled={disabled}
        // emptyMessage={`No ${label.toLowerCase()} found.`}
        emptyMessage={`Not found.`}
        fieldID={label.toLowerCase().replace(/\s+/g, "-")}
        required={false}
        // FIX 2: Pass mobileDropdownText as the label/title for the drawer header
        label={mobileDropdownText || label}
        // Also pass it as title if your component uses that specific prop
        title={mobileDropdownText || label}
        searchThreshold={10}
        searchPlaceholder={`Search${key ? " " + key : "..."}`}
        showClearButton={true}
        showDefaultBox={!isLabelInOptions && !hideLabelOption} // FIX: Pass hideLabelOption logic to mobile too
        mobileDropdownText={mobileDropdownText}
        getDisplay={getDisplay}
        displayValue={displayValue} // <--- ADDED THIS
        allchange={onChange}
        isfilter={true}
      />
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div
        className={cn(
          "relative inline-flex items-center bg-white rounded-[5px] !h-8 lg:!h-10",
          "max-w-fit", // Change from max-w-[220px]
          isActive && "border border-[var(--sahi-green)]"
        )}
      >
        <PopoverTrigger asChild>
          <Button
            aria-expanded={open}
            variant="semidefault"
            className={cn(
              // Allow the button itself to grow slightly more for these labels
              "w-auto min-w-[140px]",
              isLargeFilter ? "max-w-[300px]" : "max-w-[260px]",
              "bg-white justify-between text-black !h-8 lg:!h-10",
              !isActive ? "filter-border" : "!border-none bg-[#0b5d1e24]",
              canShowClear ? "rounded-r-none" : "rounded-r-[5px]"
            )}
          >
            <span className="truncate font-normal whitespace-nowrap text-left flex-1 mr-1">
              {getDisplay()}
            </span>
            <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        {/* {showClearButton && value && value !== "all" && (
          <div
            className={cn(
              "inline-flex items-center justify-center h-10 w-[30px] bg-[#0b5d1e24] p-1",
              "border-l border-t-0 border-r-0 border-b-0 border-[var(--sahi-green)]",
              "rounded-r-md rounded-l-none !h-8 lg:!h-10"
            )}
          >
            <div className="w-10 h-7 flex items-center justify-center">
              <X
                className="h-6 w-4 text-black cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange("all");
                }}
              />
            </div>
          </div>
        )} */}

        {canShowClear && (
          <div
            className={cn(
              "inline-flex items-center justify-center h-10 w-[30px] bg-[#0b5d1e24] p-1",
              "border-l border-[var(--sahi-green)] rounded-r-md !h-8 lg:!h-10"
            )}
          >
            <X
              className="h-4 w-4 text-black cursor-pointer "
              onClick={handleClearAction}
            />
          </div>
        )}

        <PopoverContent
          align="start"
          className={cn("p-0 bg-white text-black border border-gray-400", popoverWidth)}
        >
          <Command className="bg-white text-black">
            {(searchEnabled && flatOptions.length > 10) && (
              <CommandInput
                // placeholder={`Search ${label}`}
                placeholder={searchPlaceholder}
                className="h-9 text-black bg-white placeholder:text-gray-400"
                value={searchTerm}
                onValueChange={setSearchTerm}
              />
            )}
            <CommandList
              className="bg-white text-black max-h-[200px] overflow-y-auto w-full scrollbar-no-arrows"
              style={{
                overscrollBehavior: 'contain',
                scrollBehavior: 'smooth'
              }}
              onWheel={(e) => {
                e.stopPropagation();
              }}
            >
              <CommandEmpty>Not found.</CommandEmpty>

              {/* Render hierarchical structure if available */}
              {isHierarchical ? (
                <CommandGroup>
                  {!hideAllOption && (
                    <CommandItem
                      value="all"
                      onSelect={() => {
                        onChange("all");
                        setOpen(false);
                        setSearchTerm("");
                      }}
                      className="hover:bg-gray-100 text-sm"
                    >
                      {label}
                      <Check className={cn("ml-auto h-4 w-4", value === "all" ? "opacity-100" : "opacity-0")} />
                    </CommandItem>
                  )}

                  {categories.map((category) => (
                    <div key={category.value} className="flex flex-col">
                      <CommandItem
                        value={category.name}
                        onSelect={() => {
                          onChange(category.value);
                          setOpen(false);
                          setSearchTerm("");
                        }}
                        className="hover:bg-gray-100 text-sm font-medium px-2"
                      >
                        <span>{category.name}</span>
                        <Check className={cn("ml-auto h-4 w-4", value === category.value ? "opacity-100" : "opacity-0")} />
                      </CommandItem>

                      {category.subFilters && category.subFilters.map((subFilter) => (
                        <CommandItem
                          key={subFilter.value}
                          value={subFilter.displayValue || subFilter.name}
                          onSelect={() => {
                            onChange(subFilter.value);
                            setOpen(false);
                            setSearchTerm("");
                          }}
                          className="hover:bg-gray-100 text-sm px-8"
                        >
                          <span className="text-sm">{subFilter.displayValue || subFilter.name}</span>
                          <Check className={cn("ml-auto h-4 w-4", value === subFilter.value ? "opacity-100" : "opacity-0")} />
                        </CommandItem>
                      ))}
                    </div>
                  ))}
                </CommandGroup>
              ) : (
                <CommandGroup>
                  {!hideAllOption && !isLabelInOptions && !hideLabelOption && (
                    <CommandItem
                      value="all"
                      onSelect={() => {
                        onChange("all");
                        setOpen(false);
                        setSearchTerm("");
                      }}
                      className="hover:bg-gray-100"
                    >
                      {label}
                      <Check className={cn("ml-auto h-4 w-4", value === "all" ? "opacity-100" : "opacity-0")} />
                    </CommandItem>
                  )}
                  {filteredOptions.map((option, index) => {
                    const optionValue = getOptionValue(option);
                    const optionLabel = getOptionLabel(option);
                    const itemValue = `${optionLabel} ${optionValue} ${index}`;

                    return (
                      <CommandItem
                        key={`${optionValue}-${index}`}
                        value={itemValue}
                        onSelect={() => {
                          onChange(optionValue);
                          setOpen(false);
                          setSearchTerm("");
                        }}
                        className="hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-2 flex-1">
                          {renderOptionContent(option)}
                        </div>
                        <Check
                          className={cn(
                            "ml-auto h-4 w-4 flex-shrink-0",
                            String(value) === String(optionValue) ? "opacity-100" : "opacity-0"
                          )}
                        />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </div>
    </Popover>
  );
}